import { Translation } from '@ps/web-shared-components';
import classNames from 'clsx';
import React from 'react';

import type { FootballFixture } from '@swf/generated/graphql/scoreboards/schemas';
import EventStatus from '@swf/sports/components/EventStatus/EventStatus';
import useParticipants from '@swf/sports/hooks/useParticipants';
import { EventTypeId } from '@swf/sports/types/eventType';
import getPenaltyScore from '@swf/sports/utils/getPenaltyScore';

import Score from '../Score/Score';

import { TXTWCL_Penalties } from './Football.messages.json';
import styles from './Football.scss';

interface Props {
  name: string;
  eventTime: string;
  footballMatch?: FootballFixture;
  eventId?: number;
  hasDarkBackground?: boolean;
}

const Football: React.FC<Props> = ({
  footballMatch,
  name,
  eventTime,
  eventId,
  hasDarkBackground
}) => {
  const [homeTeamName, awayTeamName] =
    useParticipants(name)?.map(({ participant }) => participant) ?? [];
  const { score, penaltyShootout } = footballMatch || {};
  const penaltyTakingTeam = penaltyShootout?.nextTeamToShoot;
  const awayPenaltyScore = getPenaltyScore('AWAY', penaltyShootout?.penaltyScores);
  const homePenaltyScore = getPenaltyScore('HOME', penaltyShootout?.penaltyScores);

  return (
    <div
      className={classNames(styles.wrapper, {
        [styles.darkBackground]: hasDarkBackground
      })}
      data-testid="football-scoreboard"
    >
      <div className={styles.status}>
        <EventStatus
          isInPlay
          isTurnInPlay
          startTime={eventTime}
          size="XSmall"
          eventId={eventId}
          eventTypeId={EventTypeId.SOCCER}
        />
      </div>
      {penaltyShootout && (
        <div className={classNames(styles.pens)}>
          <Translation {...TXTWCL_Penalties} />
        </div>
      )}
      <div
        data-testid="football-scoreboard-home-team-name"
        className={classNames(styles.team, styles.home, {
          [styles.shooting]: penaltyTakingTeam === 'HOME'
        })}
      >
        {homeTeamName}
      </div>
      {penaltyShootout && (
        <div className={classNames(styles.score, styles.homeMatchScore)}>
          <Score score={score?.home} />
        </div>
      )}
      <div
        className={classNames(styles.score, styles.homeScore)}
        data-testid="football-scoreboard-home-score"
      >
        <Score score={penaltyShootout ? homePenaltyScore : score?.home} isHighlighted />
      </div>

      <div
        data-testid="football-scoreboard-away-team-name"
        className={classNames(styles.team, styles.away, {
          [styles.shooting]: penaltyTakingTeam === 'AWAY'
        })}
      >
        {awayTeamName}
      </div>
      {penaltyShootout && (
        <div className={classNames(styles.score, styles.awayMatchScore)}>
          <Score score={score?.away} />
        </div>
      )}
      <div
        className={classNames(styles.score, styles.awayScore)}
        data-testid="football-scoreboard-away-score"
      >
        <Score score={penaltyShootout ? awayPenaltyScore : score?.away} isHighlighted />
      </div>
    </div>
  );
};

export default Football;
